import type { ApiErrorBody, AuthResponse } from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields?: Record<string, string>;
  readonly requestId?: string;
  readonly retryAfter?: number;

  constructor(status: number, body: ApiErrorBody, requestId?: string, retryAfter?: number) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.fields = body.fields;
    this.requestId = requestId;
    this.retryAfter = retryAfter;
  }
}

/** Network failure (offline, DNS, CORS). The UI shows "Koneksi terputus" and retries. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Koneksi terputus. Periksa internet Anda lalu coba lagi.");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

export interface ClientOptions {
  /** e.g. "/api/v1" (same-origin, recommended) or "http://localhost:8080/api/v1". */
  baseUrl: string;
  /** Called when refresh fails: clear state and route to login. */
  onSessionExpired?: () => void;
  /** Called after every successful login/refresh. */
  onAuth?: (auth: AuthResponse) => void;
  fetch?: typeof fetch;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip auth header + refresh (auth endpoints, public endpoints). */
  anonymous?: boolean;
  /** Return the response body as a Blob (private files that need the Bearer token, e.g. payment receipts). */
  blob?: boolean;
}

/**
 * Thin fetch wrapper:
 * - access token kept in memory only (never localStorage),
 * - on 401 performs ONE single-flight refresh (HttpOnly cookie) and retries the request once,
 * - unwraps the { data } envelope and throws ApiError with the Indonesian message on failure.
 */
export class ApiClient {
  private accessToken: string | null = null;
  private refreshing: Promise<AuthResponse | null> | null = null;
  private readonly opts: ClientOptions;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: ClientOptions) {
    this.opts = opts;
    this.fetchImpl = opts.fetch ?? ((...args) => globalThis.fetch(...args));
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }
  get hasAccessToken() {
    return this.accessToken !== null;
  }

  /** Try to restore a session from the refresh cookie (app boot). Resolves null when logged out. */
  refresh(): Promise<AuthResponse | null> {
    if (!this.refreshing) {
      this.refreshing = this.raw<AuthResponse>("/auth/refresh", { method: "POST", anonymous: true })
        .then((auth) => {
          this.accessToken = auth.accessToken;
          this.opts.onAuth?.(auth);
          return auth;
        })
        .catch(() => {
          this.accessToken = null;
          return null;
        })
        .finally(() => {
          this.refreshing = null;
        });
    }
    return this.refreshing;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    try {
      return await this.raw<T>(path, options);
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401 || options.anonymous) throw err;
      const auth = await this.refresh();
      if (!auth) {
        this.opts.onSessionExpired?.();
        throw err;
      }
      return this.raw<T>(path, options);
    }
  }

  /** Like request() but returns the full envelope meta as well (paginated lists). */
  async requestWithMeta<T>(path: string, options: RequestOptions = {}): Promise<{ data: T; meta?: { nextCursor?: string; total?: number } }> {
    return this.request<{ data: T; meta?: { nextCursor?: string; total?: number } }>(path, { ...options, headers: { ...options.headers, "X-Envelope": "full" } });
  }

  private async raw<T>(path: string, options: RequestOptions): Promise<T> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(options.query ?? {})) {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    }
    const url = this.opts.baseUrl + path + (qs.size > 0 ? `?${qs.toString()}` : "");
    const headers: Record<string, string> = {
      Accept: "application/json",
      // Required by the API on cookie-authenticated endpoints (CSRF defense; not settable cross-site without CORS).
      "X-Requested-With": "baze",
      ...options.headers,
    };
    let body: BodyInit | undefined;
    if (options.body instanceof FormData) body = options.body;
    else if (options.body instanceof Blob) {
      headers["Content-Type"] = "application/octet-stream";
      body = options.body;
    } else if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }
    if (!options.anonymous && this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;

    let res: Response;
    try {
      res = await this.fetchImpl(url, {
        method: options.method ?? "GET",
        headers,
        body,
        credentials: "include",
        signal: options.signal,
      });
    } catch (e) {
      if ((e as Error)?.name === "AbortError") throw e;
      throw new NetworkError(e);
    }

    const requestId = res.headers.get("X-Request-Id") ?? undefined;
    if (res.status === 204) return undefined as T;
    if (res.ok && options.blob) return (await res.blob()) as T;
    const text = await res.text();
    let json: unknown = undefined;
    try {
      json = text ? JSON.parse(text) : undefined;
    } catch {
      /* non-JSON (proxy error page) */
    }
    if (!res.ok) {
      const errBody = (json as { error?: ApiErrorBody } | undefined)?.error ?? {
        code: res.status === 429 ? "rate_limited" : res.status >= 500 ? "internal" : "unknown",
        message: res.status >= 500 ? "Terjadi gangguan di server kami. Silakan coba lagi sebentar lagi." : "Permintaan tidak dapat diproses.",
      };
      const retryAfter = Number(res.headers.get("Retry-After")) || undefined;
      throw new ApiError(res.status, errBody, requestId, retryAfter);
    }
    if (options.headers?.["X-Envelope"] === "full") return json as T;
    return (json as { data: T } | undefined)?.data as T;
  }
}

/** Human message for any thrown error (for toasts). */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError || err instanceof NetworkError) return err.message;
  return "Terjadi kesalahan. Silakan coba lagi.";
}
